import React from 'react'
import { Button, Modal, Label } from '@/shared/ui'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { getHealthOverviewCached, invalidateHealthOverview } from '@/modules/crypto-assets/services/healthCache'
import { triggerRefreshHealthSnapshot } from '@/modules/crypto-assets/services/healthSnapshotService'
import { triggerBoostWarnings } from '@/modules/crypto-assets/services/boostWarningsService'
import { bulkNormalize5mSymbols, pruneExecutionQueueBuckets } from '@/modules/crypto-assets/services/bootstrapService'
import { enqueueRepair, fetchRepairCounts, fetchRepairRecent, fetchRepairTotal, cancelRepair, nudgeOhlcvRunner, listRepairItems } from '@/modules/crypto-assets/services/repairService'
import RepairJobsModal from './RepairJobsModal'
import { useToast } from '@/shared/ui/Toast'

type Props = {
  open: boolean
  onOpenChange: (v: boolean) => void
}

const TFS: string[] = ['5','15','30','60','240','720','D','W','M']

export default function HealthOverviewModal({ open, onOpenChange }: Props) {
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(false)
  const [refreshing, setRefreshing] = React.useState(false)
  const [boosting, setBoosting] = React.useState(false)
  const [onlyWarn, setOnlyWarn] = React.useState(false)
  const [rows, setRows] = React.useState<Array<{ symbol: string; warnings: number; timeframes: any[] }>>([])
  const [selected, setSelected] = React.useState<Record<string, boolean>>({})
  const [running, setRunning] = React.useState(false)
  const [progress, setProgress] = React.useState(0)
  const [repairJobId, setRepairJobId] = React.useState<string | null>(null)
  const [repairPct, setRepairPct] = React.useState(0)
  const [repairCounts, setRepairCounts] = React.useState<{ total: number; pending: number; enqueued: number; done: number; failed: number } | null>(null)
  const [showRepairDetails, setShowRepairDetails] = React.useState(false)

  async function load(force = false) {
    setLoading(true)
    try {
      const data = await getHealthOverviewCached(force)
      const arr = (data?.results || []) as any[]
      setRows(arr as any)
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    if (open) load(false)
  }, [open])

  async function refreshAll() {
    setRefreshing(true)
    try {
      await triggerRefreshHealthSnapshot().catch(() => {})
      invalidateHealthOverview()
      await load(true)
    } finally {
      setRefreshing(false)
    }
  }

  const filtered = React.useMemo(() => rows.filter(r => (onlyWarn ? (r.warnings || 0) > 0 : true)), [rows, onlyWarn])
  const allSelected = React.useMemo(() => filtered.length > 0 && filtered.every(r => selected[r.symbol]), [filtered, selected])

  function toggleAll(v: boolean) {
    const next: Record<string, boolean> = {}
    if (v) filtered.forEach(r => { next[r.symbol] = true })
    setSelected(next)
  }

  function toggleOne(sym: string, v: boolean) {
    setSelected(prev => ({ ...prev, [sym]: v }))
  }

  async function forceSelected5m() {
    const symbols = filtered.map(r => r.symbol).filter(s => selected[s])
    if (!symbols.length) return
    setRunning(true); setProgress(0)
    try {
      await bulkNormalize5mSymbols(symbols, { concurrency: 2, onProgress: (pct) => setProgress(pct) })
      invalidateHealthOverview(); await load(true)
    } finally {
      setRunning(false)
    }
  }

  async function startRepairSelectedWarnings() {
    const symbols = filtered.map(r => r.symbol).filter(s => selected[s])
    if (!symbols.length) return
    setRepairJobId(null); setRepairPct(0); setRepairCounts(null)
    try {
      const job = await enqueueRepair(symbols, true, 5, 60)
      setRepairJobId(job)
      try { localStorage.setItem('activeRepairJobId', job) } catch {}
      // Best-effort: aciona o runner para reduzir latência de início
      nudgeOhlcvRunner({ limit: 48, budgetMs: 30000 }).catch(()=>{})
      // Feedback imediato
      try {
        const total = await fetchRepairTotal(job)
        toast({ variant: 'success', title: 'Reparo iniciado', description: `Itens: ${total}. Processamento em lotes, acompanhe o progresso acima.` })
      } catch {}
    } catch {
      // noop
    }
  }

  async function startRepairAllWarnings() {
    setRepairJobId(null); setRepairPct(0); setRepairCounts(null)
    try {
      const job = await enqueueRepair(undefined, true, 5, 60)
      setRepairJobId(job)
      try { localStorage.setItem('activeRepairJobId', job) } catch {}
      nudgeOhlcvRunner({ limit: 72, budgetMs: 40000 }).catch(()=>{})
      try {
        const total = await fetchRepairTotal(job)
        toast({ variant: 'success', title: 'Reparo iniciado (todas)', description: `Itens: ${total}. O servidor vai distribuir por grupos sem sobrecarregar a CPU.` })
      } catch {}
    } catch {
      // noop
    }
  }

  React.useEffect(() => {
    if (!repairJobId) return
    let alive = true
    const tick = async () => {
      try {
        const c = await fetchRepairCounts(repairJobId)
        if (!alive) return
        setRepairCounts(c)
        let visualDone = c.done;
        try {
          const enq = await listRepairItems(repairJobId, 'enqueued', 200)
          if (enq && enq.length && rows && rows.length) {
            const map: Record<string, any> = {}
            for (const r of rows) map[r.symbol] = r
            let okExtra = 0
            for (const it of enq) {
              const rh = map[it.symbol]
              const t = (rh?.timeframes||[]).find((x:any)=> String(x.timeframe).toUpperCase()===String(it.timeframe).toUpperCase())
              const st = String(t?.status||'').toLowerCase()
              if (st === 'ok') okExtra++
            }
            visualDone = Math.min(c.total, c.done + okExtra)
          }
        } catch {} const pct = c.total ? Math.round((visualDone / c.total) * 100) : 0
        setRepairPct(pct)
        // últimos itens
        try {
          const last = await fetchRepairRecent(repairJobId, 5)
          if (alive) setLastItems(last)
        } catch {}
        if (c.total && (c.done + c.failed) >= c.total) {
          invalidateHealthOverview(); await load(true)
          toast({ variant: 'success', title: 'Reparo concluído', description: `Processados ${c.done} itens${c.failed ? `, ${c.failed} falharam` : ''}.` })
          try { localStorage.removeItem('activeRepairJobId') } catch {}
          }
      } catch {
        // noop
      }
    }
    tick()
    const id = setInterval(tick, 5000)
    return () => { alive = false; clearInterval(id) }
  }, [repairJobId])

  async function pruneBuckets() {
    setRunning(true)
    try {
      await pruneExecutionQueueBuckets({ timeframe: '5' })
      invalidateHealthOverview(); await load(true)
    } finally {
      setRunning(false)
    }
  }

  const etaState = React.useMemo(() => {
    if (!repairCounts) return { sec: 0, label: '—' }
    const limitPerTick = 60
    const groups = 5
    const { pending, enqueued } = repairCounts
    if (pending > 0) {
      const sec = Math.ceil((pending / limitPerTick) * groups * 60)
      return { sec, label: `${sec}s` }
    }
    if (pending === 0 && enqueued > 0) {
      // Já na fila; aguarda runner (cron por minuto). Mostra teto amigável
      return { sec: 60, label: '≤ 60s' }
    }
    return { sec: 0, label: '—' }
  }, [repairCounts])

  function fmtEta(sec: number) {
    if (!sec || sec < 0) return '—'
    if (sec < 60) return `${sec}s`
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return `${m}m ${s}s`
  }

  const [lastItems, setLastItems] = React.useState<Array<{ symbol: string; timeframe: string; status: string; updated_at: string }>>([])
  const [jobsModalOpen, setJobsModalOpen] = React.useState(false)

  const repairState = React.useMemo(() => {
    if (!repairCounts) return { label: '', tone: 'muted' as const }
    const { total, pending, enqueued, done, failed } = repairCounts
    if (total && (done + failed) >= total) return { label: 'Reparo concluído', tone: 'success' as const }
    if (enqueued > 0) return { label: 'Processando no servidor…', tone: 'progress' as const }
    if (pending > 0) return { label: 'Aguardando agendamento…', tone: 'muted' as const }
    return { label: 'Iniciando…', tone: 'muted' as const }
  }, [repairCounts])

  return (
    <>
    <Modal open={open} onOpenChange={onOpenChange} className="p-4 sm:p-6" style={{ width: 1200, maxWidth: 1200 }}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-lg font-semibold">Saúde geral (todos os timeframes)</div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <div className="flex items-center gap-2 text-xs">
            <input id="only-warn" type="checkbox" className="h-4 w-4" checked={onlyWarn} onChange={(e) => setOnlyWarn(e.currentTarget.checked)} />
            <Label htmlFor="only-warn" className="text-xs">Somente warnings</Label>
          </div>

          <Button variant="default" onClick={startRepairSelectedWarnings} disabled={!filtered.some(r => selected[r.symbol])}>Corrigir (selecionadas)</Button>
          <Button variant="secondary" onClick={startRepairAllWarnings}>Corrigir (todas)</Button>
          <Button variant="outline" onClick={refreshAll} disabled={refreshing}>{refreshing ? 'Atualizando…' : 'Atualizar saúde'}</Button>

          <DropdownMenu
            trigger={<Button variant="outline">Ações</Button>}
            items={[
              { label: 'Forçar 5m (selecionadas)', onSelect: () => forceSelected5m(), disabled: running || !filtered.some(r => selected[r.symbol]) },
              { label: 'Zerar fila (buckets)', onSelect: () => pruneBuckets(), disabled: running },
              { label: boosting ? 'Boosting…' : 'Boost warnings', onSelect: async () => { try { setBoosting(true); await triggerBoostWarnings({ mode: 'queue', max: 60, concurrency: 1 }); invalidateHealthOverview(); await load(true) } finally { setBoosting(false) } }, disabled: boosting },
              { label: 'Fila de reparos', onSelect: () => setJobsModalOpen(true), disabled: false },
            ]}
            align="end"
          />
        </div>
      </div>

      {repairJobId && (
        <div className="mb-3 w-full">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-foreground/80">
            <div className="flex items-center gap-2">
              {repairState.tone === 'success' ? (
                <span className="inline-flex items-center rounded bg-emerald-500/15 px-2 py-0.5 text-emerald-400">{repairState.label}</span>
              ) : (
                <span className="inline-flex items-center rounded bg-foreground/10 px-2 py-0.5 text-foreground/80">{repairState.label || 'Reparo em andamento'}</span>
              )}
              <button className="underline decoration-dotted underline-offset-4 opacity-80 hover:opacity-100" onClick={()=> setShowRepairDetails(v=>!v)}>
                {showRepairDetails ? 'Ocultar detalhes' : 'Ver detalhes'}
              </button>
            </div>
            {repairCounts && (
              <div className="flex items-center gap-2">
                <span>Concluídos: {repairCounts.done} de {repairCounts.total}</span>
                <span className="text-foreground/60">•</span>
                <span>Falhas: {repairCounts.failed}</span>
                <span className="text-foreground/60">•</span>
                <span>ETA ~ {etaState.label}</span>
              </div>
            )}
          </div>
          {showRepairDetails && repairCounts && (
            <div className="mt-1 text-[11px] text-foreground/60">
              Job: <span className="font-mono text-foreground/70">{repairJobId}</span>
              <span className="mx-2">•</span>
              Pendentes: {repairCounts.pending} • Na fila: {repairCounts.enqueued}
              <button className="ml-3 rounded border border-border px-2 py-0.5 text-foreground/80 hover:bg-foreground/5" onClick={async ()=>{
                if (!repairJobId) return
                try { await cancelRepair(repairJobId); toast({ variant: 'info', title: 'Job cancelado', description: 'Itens pendentes foram ignorados.' }); try { localStorage.removeItem('activeRepairJobId') } catch {}; setRepairJobId(null); setRepairCounts(null); setRepairPct(0) } catch (e:any) { toast({ variant:'error', title:'Falha ao cancelar', description: e?.message || 'Tente novamente' }) }
              }}>Cancelar</button>
            </div>
          )}
          <div className="mt-2 h-2 w-full overflow-hidden rounded bg-foreground/10">
            <div className="h-2 rounded bg-emerald-500 transition-all" style={{ width: `${repairPct}%` }} />
          </div>
          {lastItems && lastItems.length > 0 && (
            <div className="mt-2 text-[11px] text-foreground/60">
              Últimas ações: {lastItems.slice(0,5).map((it,i)=> (
                <span key={i} className="mr-2">{it.symbol} {it.timeframe} <span className="opacity-70">→ {it.status}</span></span>
              ))}
            </div>
          )}
        </div>
      )}

      {running && (
        <div className="mb-3 w-full">
          <div className="h-2 w-full rounded bg-foreground/10">
            <div className="h-2 rounded bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
          </div>
          <div className="mt-1 text-xs text-foreground/70">Normalizando 5m… {progress}%</div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-foreground/60">Carregando…</div>
      ) : (
        <div className="max-h-[70vh] overflow-auto rounded-md border border-border/50">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background">
              <tr className="text-left">
                <th className="p-2"><input type="checkbox" className="h-4 w-4" checked={allSelected} onChange={(e) => toggleAll(e.currentTarget.checked)} /></th>
                <th className="p-2">Símbolo</th>
                {TFS.map(tf => (<th key={tf} className="p-2">{tf}</th>))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.symbol} className="border-t border-border/50">
                  <td className="p-2"><input type="checkbox" className="h-4 w-4" checked={!!selected[r.symbol]} onChange={(e) => toggleOne(r.symbol, e.currentTarget.checked)} /></td>
                  <td className="p-2 font-medium">{r.symbol}</td>
                  {TFS.map(tf => {
                    const t = (r.timeframes || []).find((x: any) => String(x.timeframe) === tf)
                    const st = (t?.status || '').toLowerCase()
                    const cls = st === 'ok' ? 'text-emerald-500' : (st ? 'text-amber-500' : 'text-foreground/60')
                    const cnt = typeof t?.count === 'number' ? t.count : 0
                    return (
                      <td key={tf} className="p-2">
                        <span className={cls}>{cnt || 0}</span>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex items-center justify-end">
        <Button variant="ghost" onClick={() => onOpenChange(false)}>Fechar</Button>
      </div>
    </Modal>
    {jobsModalOpen && (<RepairJobsModal open={jobsModalOpen} onOpenChange={setJobsModalOpen} />)}
    </>
  )
}






